import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthApiService } from '@api-new/authservice';
import { RoutePathsEnum } from '@app/app.utils';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ErrorModel } from '@shared-lib/models/error.model';
import { ToastService, ToastSeverityEnum } from '@shared-lib/services/toast.service';
import { loadAdvisorUserData } from '@shared/store/platform/platform.actions';
import { getUserIdFromToken } from '@shared/utils/get-user-id-from-token';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import {
  authError,
  loginAction,
  loginFailureAction,
  loginSuccessAction,
  loginWithTokenAction,
  loginWithTokenFromSSOAction,
  logoutAction,
} from './auth.action';

@Injectable()
export class AuthEffect {
  private readonly actions$ = inject(Actions);
  private readonly authService = inject(AuthService);
  private readonly authApiService = inject(AuthApiService);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly store = inject(Store);

  login = createEffect(() => {
    return this.actions$.pipe(
      ofType(loginAction),
      tap(() => {
        this.authService.clearRedirectUrl();
      }),
      switchMap(({ email, password }) =>
        this.authApiService
          .HTTP_AP_SignIn_WithPassword({
            email,
            password,
          })
          .pipe(
            map(({ customToken }: { customToken: string }) => {
              const userId = getUserIdFromToken(customToken);
              return loginSuccessAction({ token: customToken, uid: userId });
            }),
            catchError((error: ErrorModel) => {
              return of(loginFailureAction({ error }));
            }),
          ),
      ),
    );
  });

  loginWithToken = createEffect(() => {
    return this.actions$.pipe(
      ofType(loginWithTokenAction),
      tap(() => {
        this.authService.clearRedirectUrl();
      }),
      switchMap(({ customToken }: { customToken: string }) => {
        const userId = getUserIdFromToken(customToken);
        return of(loginSuccessAction({ token: customToken, uid: userId }));
      }),
      catchError((error: ErrorModel) => {
        return of(loginFailureAction({ error }));
      }),
    );
  });

  loginWithTokenFromSSO = createEffect(() => {
    return this.actions$.pipe(
      ofType(loginWithTokenFromSSOAction),
      switchMap(({ customToken }: { customToken: string }) => {
        const userId = getUserIdFromToken(customToken);
        return of(loginSuccessAction({ token: customToken, uid: userId }));
      }),
      catchError((error: ErrorModel) => {
        return of(loginFailureAction({ error }));
      }),
    );
  });

  logout = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(logoutAction),
        tap(() => {
          this.authService.logout();
          void this.router.navigate([RoutePathsEnum.Landing, RoutePathsEnum.Login]);
        }),
      );
    },
    { dispatch: false },
  );

  loginSuccess = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(loginSuccessAction),
        tap(({ token, uid }) => {
          this.store.dispatch(loadAdvisorUserData());
          this.authService.setAccessToken(token);
          const redirectUrl = this.authService.getRedirectUrl();
          if (redirectUrl) {
            void this.router.navigate([redirectUrl]);
            this.authService.clearRedirectUrl();
            return;
          }
          void this.router.navigate([RoutePathsEnum.Platform]);
        }),
      );
    },
    { dispatch: false },
  );

  loginFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loginFailureAction),
      map(({ error }: { error: ErrorModel }) => {
        this.toastService.showToast(ToastSeverityEnum.Error, error.error.customErrorMessage, { summary: 'Login error' });
        return authError({ customErrorMessage: error.error.customErrorMessage });
      }),
    );
  });
}
